import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getInventory } from "../../services/inventoryService";
import {getProducts} from "../../services/productService";
import {
    AiOutlineDatabase,
    AiOutlineWarning,
    AiOutlineDollarCircle,
    AiOutlineBarChart,
    AiOutlineReload
} from "react-icons/ai";

function InventoryStats() {
    const [stats, setStats] = useState({
        totalProducts: 0,
        lowStockItems: 0,
        totalValue: 0,
        mostStockedCategory: "N/A",
        stockDistribution: {},
        averageStockLevel: 0
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setIsLoading(true);
        try {
            // Fetch inventory and product data
            const inventoryResponse = await getInventory();
            const productsResponse = await getProducts();

            const inventoryItems = Array.isArray(inventoryResponse.data)
                ? inventoryResponse.data
                : [];

            const products = Array.isArray(productsResponse)
                ? productsResponse
                : (Array.isArray(productsResponse?.data) ? productsResponse.data : []);

            // Calculate total products
            const totalProducts = inventoryItems.length;

            // Calculate low stock items
            const lowStockItems = inventoryItems.filter(item =>
                item.quantity <= item.reorder_level
            ).length;

            // Calculate total inventory value
            let totalValue = 0;
            const categoryCounts = {};
            let totalQuantity = 0;

            inventoryItems.forEach(item => {
                const product = products.find(p => p.id === item.product_id);
                if (product) {
                    // Add to total value
                    const itemValue = item.quantity * parseFloat(product.price || 0);
                    totalValue += itemValue;

                    // Count items by category
                    const category = product.category_name || 'Uncategorized';
                    if (!categoryCounts[category]) {
                        categoryCounts[category] = {
                            count: 0,
                            items: 0
                        };
                    }
                    categoryCounts[category].count += 1;
                    categoryCounts[category].items += item.quantity;

                    // Add to total quantity
                    totalQuantity += item.quantity;
                }
            });

            // Find most stocked category
            let mostStockedCategory = "N/A";
            let maxItems = 0;

            Object.entries(categoryCounts).forEach(([category, data]) => {
                if (data.items > maxItems) {
                    mostStockedCategory = category;
                    maxItems = data.items;
                }
            });

            // Calculate average stock level
            const averageStockLevel = totalProducts > 0
                ? Math.round(totalQuantity / totalProducts)
                : 0;

            // Calculate stock distribution for chart
            const stockDistribution = {};
            Object.entries(categoryCounts).forEach(([category, data]) => {
                stockDistribution[category] = Math.round((data.items / totalQuantity) * 100);
            });

            setStats({
                totalProducts,
                lowStockItems,
                totalValue,
                mostStockedCategory,
                stockDistribution,
                averageStockLevel
            });
        } catch (error) {
            console.error("Error fetching inventory stats:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container>
            <Header>
                <h2>Inventory Statistics</h2>
                <RefreshButton onClick={fetchStats}>
                    <AiOutlineReload />
                </RefreshButton>
            </Header>

            {isLoading ? (
                <LoadingState>Loading inventory statistics...</LoadingState>
            ) : (
                <StatsGrid>
                    <StatCard>
                        <StatIcon className="total">
                            <AiOutlineDatabase />
                        </StatIcon>
                        <StatInfo>
                            <StatValue>{stats.totalProducts}</StatValue>
                            <StatLabel>Total Products</StatLabel>
                        </StatInfo>
                    </StatCard>

                    <StatCard>
                        <StatIcon className="warning">
                            <AiOutlineWarning />
                        </StatIcon>
                        <StatInfo>
                            <StatValue>{stats.lowStockItems}</StatValue>
                            <StatLabel>Low Stock Items</StatLabel>
                        </StatInfo>
                    </StatCard>

                    <StatCard>
                        <StatIcon className="value">
                            <AiOutlineDollarCircle />
                        </StatIcon>
                        <StatInfo>
                            <StatValue>Rs.{stats.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</StatValue>
                            <StatLabel>Total Inventory Value</StatLabel>
                        </StatInfo>
                    </StatCard>

                    <StatCard>
                        <StatIcon className="average">
                            <AiOutlineBarChart />
                        </StatIcon>
                        <StatInfo>
                            <StatValue>{stats.averageStockLevel}</StatValue>
                            <StatLabel>Avg. Stock per Product</StatLabel>
                        </StatInfo>
                    </StatCard>
                </StatsGrid>
            )}

            <AdditionalStats>
                <StockSummary>
                    <h3>Stock Distribution</h3>
                    {Object.keys(stats.stockDistribution).length > 0 ? (
                        <DistributionChart>
                            {Object.entries(stats.stockDistribution)
                                .sort((a, b) => b[1] - a[1])
                                .map(([category, percentage], index) => (
                                    <CategoryBar key={category}>
                                        <CategoryLabel>{category}</CategoryLabel>
                                        <BarContainer>
                                            <Bar
                                                percentage={percentage}
                                                color={getColorForIndex(index)}
                                            />
                                            <BarLabel>{percentage}%</BarLabel>
                                        </BarContainer>
                                    </CategoryBar>
                                ))
                            }
                        </DistributionChart>
                    ) : (
                        <EmptyState>No distribution data available</EmptyState>
                    )}
                </StockSummary>

                <MostStocked>
                    <h3>Most Stocked Category</h3>
                    <LeadingCategory>
                        {stats.mostStockedCategory !== "N/A" ? (
                            <CategoryName>{stats.mostStockedCategory}</CategoryName>
                        ) : (
                            <EmptyState>No category data available</EmptyState>
                        )}
                    </LeadingCategory>
                </MostStocked>
            </AdditionalStats>
        </Container>
    );
}

// Helper function to get colors for bars
const getColorForIndex = (index) => {
    const colors = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#607D8B'];
    return colors[index % colors.length];
};

export default InventoryStats;

const Container = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 {
        font-size: 18px;
        margin: 0;
        color: #333;
    }
`;

const RefreshButton = styled.button`
    background: none;
    border: none;
    color: #777;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #000;
    }
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 30px;
    color: #777;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
`;

const StatCard = styled.div`
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const StatIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    margin-right: 12px;
    font-size: 20px;
    
    &.total {
        background-color: #e3f2fd;
        color: #2196F3;
    }
    
    &.warning {
        background-color: #fff3e0;
        color: #ff9800;
    }
    
    &.value {
        background-color: #e8f5e9;
        color: #4caf50;
    }
    
    &.average {
        background-color: #f3e5f5;
        color: #9c27b0;
    }
`;

const StatInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatValue = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #333;
`;

const StatLabel = styled.div`
    font-size: 12px;
    color: #777;
    margin-top: 3px;
`;

const AdditionalStats = styled.div`
    display: grid;
    grid-template-columns: 3fr 1fr;
    gap: 15px;
    margin-top: 20px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
    
    h3 {
        font-size: 14px;
        margin: 0 0 15px 0;
        color: #555;
    }
`;

const StockSummary = styled.div`
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 15px;
`;

const MostStocked = styled.div`
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 15px;
`;

const DistributionChart = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const CategoryBar = styled.div`
    display: flex;
    align-items: center;
`;

const CategoryLabel = styled.div`
    width: 100px;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const BarContainer = styled.div`
    flex-grow: 1;
    display: flex;
    align-items: center;
`;

const Bar = styled.div`
    height: 14px;
    width: ${props => props.percentage}%;
    background-color: ${props => props.color || '#2196F3'};
    border-radius: 7px;
    min-width: 10px;
`;

const BarLabel = styled.div`
    margin-left: 8px;
    font-size: 12px;
    color: #666;
`;

const LeadingCategory = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
`;

const CategoryName = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: center;
    padding: 15px;
    background-color: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    width: 100%;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 15px;
    color: #999;
    font-style: italic;
    font-size: 13px;
    background-color: white;
    border-radius: 4px;
`;